/**
 * バディシステム用Composable
 * 担任・バディ・プールの概念を管理
 */

import { ref, computed } from 'vue'
import { supabase } from '../supabaseClient'

export function useBuddySystem() {
  // 状態管理
  const currentInstructor = ref(null)
  const instructorGroups = ref([])
  const instructorAssignments = ref([])
  const activeAbsences = ref([])
  const isLoading = ref(false)

  /**
   * 現在の講師情報を取得
   */
  const loadCurrentInstructor = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('認証が必要です')
      }
      currentInstructor.value = user
      return user
    } catch (error) {
      console.error('講師情報取得エラー:', error)
      throw error
    }
  }

  /**
   * 講師の所属グループを取得
   */
  const loadInstructorGroups = async (instructorId) => {
    try {
      const { data, error } = await supabase
        .from('instructor_group_members')
        .select(`
          group_id,
          role,
          instructor_groups (
            id,
            name,
            description
          )
        `)
        .eq('instructor_id', instructorId)

      if (error) throw error

      instructorGroups.value = (data || []).map(item => ({
        groupId: item.group_id,
        role: item.role,
        group: item.instructor_groups
      }))

      return instructorGroups.value
    } catch (error) {
      console.error('グループ情報取得エラー:', error)
      throw error
    }
  }

  /**
   * 講師の担当生徒を取得
   */
  const loadInstructorAssignments = async (instructorId) => {
    try {
      const { data, error } = await supabase
        .from('instructor_assignments')
        .select(`
          learner_user_id,
          group_id,
          note,
          users_phase2:learner_user_id (
            id,
            full_name
          )
        `)
        .eq('primary_instructor_id', instructorId)

      if (error) throw error

      instructorAssignments.value = data || []
      return instructorAssignments.value
    } catch (error) {
      console.error('担当生徒取得エラー:', error)
      throw error
    }
  }

  /**
   * アクティブな休暇・緊急事態を取得
   */
  const loadActiveAbsences = async (instructorId) => {
    try {
      const today = new Date().toISOString().split('T')[0]
      
      const { data, error } = await supabase
        .from('instructor_absences')
        .select('*')
        .eq('instructor_id', instructorId)
        .eq('is_active', true)
        .lte('start_date', today)
        .gte('end_date', today)

      if (error) throw error

      activeAbsences.value = data || []
      return activeAbsences.value
    } catch (error) {
      console.error('休暇情報取得エラー:', error)
      throw error
    }
  }

  /**
   * バディグループのメンバーを取得
   */
  const loadBuddyGroupMembers = async (groupId) => {
    try {
      const { data, error } = await supabase
        .from('instructor_group_members')
        .select(`
          instructor_id,
          role,
          users_phase2 (
            id,
            full_name,
            email
          )
        `)
        .eq('group_id', groupId)

      if (error) throw error

      return (data || []).map(item => ({
        instructorId: item.instructor_id,
        role: item.role,
        instructor: item.users_phase2
      }))
    } catch (error) {
      console.error('バディメンバー取得エラー:', error)
      throw error
    }
  }

  /**
   * 現在時刻をJSTで取得
   * @returns {Object} { jstHour, jstMinutes, now } - UI表示の残り時間計算に流用可能
   */
  const getCurrentJSTTime = () => {
    const now = new Date()
    // JSTの"時"を直接算出（DST非考慮でOK）
    const jstHour = (now.getUTCHours() + 9) % 24
    // JSTの"分"も算出（サーバークロックの揺れに備える）
    const jstMinutes = (now.getUTCMinutes() + 0) % 60
    return { jstHour, jstMinutes, now }
  }

  /**
   * 提出データのスコープを決定
   */
  const getSubmissionScope = computed(() => {
    if (!currentInstructor.value) return 'none'

    // 休暇・緊急事態のチェック
    const hasActiveAbsence = activeAbsences.value.length > 0
    if (hasActiveAbsence) {
      return 'pool' // 休暇中はプールのみ
    }

    // 14:00以降はプール（JST固定）
    const { jstHour } = getCurrentJSTTime()
    if (jstHour >= 14) {
      return 'pool'
    }

    // 通常時は担任 + バディ
    return 'assigned_and_buddy'
  })

  /**
   * 提出データのフィルター条件を取得
   */
  const getSubmissionFilters = computed(() => {
    const scope = getSubmissionScope.value
    const instructorId = currentInstructor.value?.id

    if (!instructorId) return null

    switch (scope) {
      case 'assigned_and_buddy':
        // 担任の生徒 + バディグループの生徒
        const assignedLearnerIds = instructorAssignments.value.map(a => a.learner_user_id)
        const buddyGroupIds = instructorGroups.value.map(g => g.groupId)
        
        return {
          type: 'assigned_and_buddy',
          assignedLearnerIds,
          buddyGroupIds,
          instructorId
        }

      case 'pool':
        // 全生徒（プール）
        return {
          type: 'pool',
          instructorId
        }

      default:
        return null
    }
  })

  /**
   * バディ対象の生徒IDを取得
   */
  const getBuddyLearnerIds = async (instructorId) => {
    try {
      // 自分の所属グループを取得
      const myGroups = instructorGroups.value.map(g => g.groupId)
      if (myGroups.length === 0) return []

      // バディ対象の生徒IDを取得（グループ粒度で絞り込み）
      const { data, error } = await supabase
        .from('buddy_learners_today_by_group')
        .select('learner_user_id')
        .in('group_id', myGroups)

      if (error) throw error

      return (data || []).map(item => item.learner_user_id)
    } catch (error) {
      console.error('バディ対象取得エラー:', error)
      return []
    }
  }

  /**
   * 提出データを取得（最適化版）
   */
  const loadSubmissionsOptimized = async (filters) => {
    if (!filters) return []

    try {
      let query = supabase
        .from('submissions_v3')
        .select(`
          *,
          learners (
            user_id,
            name,
            name_kana
          )
        `)
        .eq('status', 'submitted') // FB待ちのみ
        .order('created_at', { ascending: false })

      // スコープ別フィルター
      if (filters.type === 'assigned_and_buddy') {
        // 担任の生徒 + バディ対象の生徒
        const assignedLearnerIds = filters.assignedLearnerIds || []
        const buddyLearnerIds = await getBuddyLearnerIds(filters.instructorId)
        
        // 重複排除してunion
        const allLearnerIds = [...new Set([...assignedLearnerIds, ...buddyLearnerIds])]
        
        if (allLearnerIds.length > 0) {
          query = query.in('user_id', allLearnerIds)
        } else {
          // 担当生徒もバディ対象もいない場合は空配列を返す
          return []
        }
      }
      // poolの場合はフィルターなし（全生徒）

      // 期間制限（過去3日/72時間）
      const since = new Date(Date.now() - 72 * 60 * 60 * 1000)
      query = query.gte('created_at', since.toISOString())

      // 件数制限
      query = query.limit(1000)

      const { data, error } = await query

      if (error) throw error

      return data || []
    } catch (error) {
      console.error('提出データ取得エラー:', error)
      throw error
    }
  }

  /**
   * 期限切れロックを掃除
   */
  const cleanupExpiredLocks = async () => {
    try {
      const { error } = await supabase.rpc('cleanup_expired_locks')
      if (error) throw error
    } catch (error) {
      console.error('期限切れロック掃除エラー:', error)
    }
  }

  /**
   * FBロック状態をチェック
   */
  const checkFeedbackLock = async (submissionId) => {
    try {
      // まず期限切れロックを掃除
      await cleanupExpiredLocks()

      const { data, error } = await supabase
        .from('feedback_locks')
        .select('*')
        .eq('submission_id', submissionId)
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString())

      if (error) throw error

      return data && data.length > 0 ? data[0] : null
    } catch (error) {
      console.error('FBロック確認エラー:', error)
      return null
    }
  }

  /**
   * FBロックを設定（軽いトランザクション処理）
   */
  const setFeedbackLock = async (submissionId, instructorId) => {
    try {
      // 軽いトランザクション処理
      const { data, error } = await supabase.rpc('set_feedback_lock_with_transaction', {
        p_submission_id: submissionId,
        p_instructor_id: instructorId
      })

      if (error) {
        // ユニーク制約違反の場合
        if (error.code === '23505') {
          // 残り時間を取得して表示
          const remainingTime = await getRemainingLockTime(submissionId)
          throw new Error(`⚠️ 他の講師が対応中（${remainingTime}分後に自動解除）`)
        }
        throw error
      }

      return data
    } catch (error) {
      console.error('FBロック設定エラー:', error)
      throw error
    }
  }

  /**
   * ロックの残り時間を取得
   */
  const getRemainingLockTime = async (submissionId) => {
    try {
      const { data, error } = await supabase
        .from('feedback_locks')
        .select('expires_at')
        .eq('submission_id', submissionId)
        .eq('is_active', true)
        .single()

      if (error || !data) return 0

      const expiresAt = new Date(data.expires_at)
      const now = new Date()
      const remainingMs = expiresAt.getTime() - now.getTime()
      const remainingMinutes = Math.ceil(remainingMs / (1000 * 60))

      return Math.max(0, remainingMinutes)
    } catch (error) {
      console.error('残り時間取得エラー:', error)
      return 0
    }
  }

  /**
   * FBロックを解除
   */
  const releaseFeedbackLock = async (submissionId, instructorId) => {
    try {
      const { error } = await supabase
        .from('feedback_locks')
        .update({ is_active: false })
        .eq('submission_id', submissionId)
        .eq('instructor_id', instructorId)
        .eq('is_active', true)

      if (error) throw error

      return true
    } catch (error) {
      console.error('FBロック解除エラー:', error)
      return false
    }
  }

  /**
   * 初期化
   */
  const initialize = async () => {
    isLoading.value = true
    try {
      const instructor = await loadCurrentInstructor()
      await Promise.all([
        loadInstructorGroups(instructor.id),
        loadInstructorAssignments(instructor.id),
        loadActiveAbsences(instructor.id)
      ])
    } catch (error) {
      console.error('バディシステム初期化エラー:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  return {
    // 状態
    currentInstructor,
    instructorGroups,
    instructorAssignments,
    activeAbsences,
    isLoading,

    // 計算プロパティ
    getSubmissionScope,
    getSubmissionFilters,

    // メソッド
    loadCurrentInstructor,
    loadInstructorGroups,
    loadInstructorAssignments,
    loadActiveAbsences,
    loadBuddyGroupMembers,
    getBuddyLearnerIds,
    loadSubmissionsOptimized,
    cleanupExpiredLocks,
    checkFeedbackLock,
    setFeedbackLock,
    getRemainingLockTime,
    releaseFeedbackLock,
    getCurrentJSTTime,
    initialize
  }
}
